import React, { ErrorInfo, ReactNode } from 'react';

import { logger } from '@/utils/logger';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  /** Reloads the document; injectable so tests need not patch `window.location`. */
  reload?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * React error boundary that catches rendering errors in its subtree.
 *
 * When an error is caught, renders a fallback UI (either the provided
 * `fallback` prop or a default error message). Logs the error via the
 * application logger for debugging.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public override state: ErrorBoundaryState = { hasError: false };

  private handleReload = (): void => {
    const { reload } = this.props;
    if (reload) {
      reload();
      return;
    }
    window.location.reload();
  };

  /**
   * React lifecycle method that updates state when a descendant throws an error.
   *
   * Sets `hasError` to true so the next render displays the fallback UI
   * instead of the broken component tree.
   *
   * @returns The updated error boundary state with `hasError` set to true.
   */
  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  /**
   * React lifecycle method invoked after an error is caught during rendering.
   *
   * Logs the error and its component stack trace via the application logger
   * for debugging and monitoring purposes.
   *
   * @param error - The error that was thrown.
   * @param info - An object containing the component stack trace.
   */
  override componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error('Uncaught error:', error, info);
  }

  /**
   * Renders the fallback UI when an error has been caught, or the children when
   * the component tree is healthy.
   *
   * When `hasError` is true, renders the custom `fallback` prop if provided,
   * otherwise renders a default error message with a reload button. A reload is
   * the one recovery that always exists at this level — this boundary is the
   * outermost one, so there is no narrower subtree to retry.
   *
   * @returns The fallback UI or the children component tree.
   */
  override render(): ReactNode {
    const { hasError } = this.state;
    const { fallback, children } = this.props;

    if (hasError) {
      return (
        fallback ?? (
          <div role="alert" className="flex flex-col items-center justify-center p-4 text-destructive-text">
            <h2 className="mb-2 text-lg font-semibold">Something went wrong.</h2>
            <p className="mb-4">Reloading usually fixes it. If it keeps happening, contact support.</p>
            <button
              type="button"
              onClick={this.handleReload}
              className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Reload page
            </button>
          </div>
        )
      );
    }

    return children;
  }
}

export default ErrorBoundary;
